/**
 * src/components/SyncIndicator.tsx
 *
 * Small floating pill that surfaces MutationQueue activity:
 *   - 1+ pending mutations  → "Syncing N change[s]…" (neutral)
 *   - 0 pending + 1+ failed → "N change[s] didn't sync — tap to retry"
 *   - empty queue           → renders nothing
 *
 * Subscribes to `subscribeToMutationQueue` so the count stays current
 * without a polling loop. Mount once at the App shell level — the queue
 * is module-global so multiple instances would just duplicate listeners.
 *
 * ROADMAP §6.2 final sub-item.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  subscribeToMutationQueue,
  retryFailedMutation,
  dismissFailedMutation,
  type MutationQueueSnapshot,
} from '../api/mutationQueue';

const EMPTY: MutationQueueSnapshot = { pending: [], failed: [] };

export function SyncIndicator() {
  const [snap, setSnap] = useState<MutationQueueSnapshot>(EMPTY);

  useEffect(() => {
    const unsub = subscribeToMutationQueue(setSnap);
    return unsub;
  }, []);

  const pending = snap.pending.length;
  const failed = snap.failed.length;

  const onRetryAll = useCallback(() => {
    for (const f of snap.failed) retryFailedMutation(f.id);
  }, [snap.failed]);

  const onDismissAll = useCallback(() => {
    for (const f of snap.failed) dismissFailedMutation(f.id);
  }, [snap.failed]);

  if (pending === 0 && failed === 0) return null;

  // Color tokens: pending = neutral surface, failed = warning. Position
  // is bottom-left so we don't collide with FeedbackWidget (bottom-right)
  // or the BottomTabBar on mobile.
  const showFailed = pending === 0 && failed > 0;
  const label = showFailed
    ? `${failed} change${failed === 1 ? '' : 's'} didn't sync`
    : `Syncing ${pending} change${pending === 1 ? '' : 's'}…`;

  return (
    <div
      data-testid="sync-indicator"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
        left: 'var(--space-3)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '8px 14px',
        borderRadius: 'var(--radius-pill)',
        background: showFailed ? 'var(--color-warning-surface)' : 'var(--color-surface-raised)',
        border: '1px solid ' + (showFailed ? 'var(--color-warning-border)' : 'var(--color-border)'),
        color: 'var(--color-text-primary)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 500,
        boxShadow: 'var(--shadow-1)',
        maxWidth: 'calc(100vw - var(--space-6))',
      }}
    >
      <span data-testid="sync-indicator-label">{label}</span>
      {showFailed && (
        <>
          <button
            type="button"
            data-testid="sync-indicator-retry"
            onClick={onRetryAll}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '2px 6px',
              fontSize: 'var(--fs-xs)',
              fontFamily: 'inherit',
            }}
          >
            Retry
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            data-testid="sync-indicator-dismiss"
            onClick={onDismissAll}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: '2px 6px',
              fontSize: 'var(--fs-xs)',
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}
